import { forwardRef } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '../../lib/cn'

type Variant = 'primary' | 'accent' | 'outline' | 'ghost-light'
type Size = 'sm' | 'md' | 'lg'

interface BaseProps {
  variant?: Variant
  size?: Size
  className?: string
  children: React.ReactNode
}

const variantClass: Record<Variant, string> = {
  primary: 'btn-primary',
  accent: 'btn-accent',
  outline: 'btn-outline',
  'ghost-light': 'btn-ghost-light',
}
const sizeClass: Record<Size, string> = { sm: 'btn-sm', md: 'btn-md', lg: 'btn-lg' }

function classes(v: Variant, s: Size, extra?: string) {
  return cn('btn', variantClass[v], sizeClass[s], extra)
}

type ButtonAsButton = BaseProps &
  React.ButtonHTMLAttributes<HTMLButtonElement> & { to?: undefined; href?: undefined }
type ButtonAsLink = BaseProps & { to: string } & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>
type ButtonAsAnchor = BaseProps & { href: string } & React.AnchorHTMLAttributes<HTMLAnchorElement>
type ButtonProps = ButtonAsButton | ButtonAsLink | ButtonAsAnchor

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', className, children, ...rest },
  _ref,
) {
  const cls = classes(variant, size, className)

  if ('to' in rest && rest.to !== undefined) {
    const { to, ...linkRest } = rest as ButtonAsLink
    return (
      <Link to={to} className={cls} {...linkRest}>
        {children}
      </Link>
    )
  }
  if ('href' in rest && rest.href !== undefined) {
    const anchorRest = rest as ButtonAsAnchor
    return (
      <a className={cls} {...anchorRest}>
        {children}
      </a>
    )
  }
  const buttonRest = rest as ButtonAsButton
  return (
    <button className={cls} {...buttonRest}>
      {children}
    </button>
  )
})
